import { useMemo } from "react";

/**
 * UserAvatar — single source of truth for rendering a user's profile photo
 * across Soul Circle, the feed, DMs, friend list, and the soul profile page.
 *
 * Falls back to a soft gradient + capitalized first letter when no photo is
 * uploaded, so we never render a broken <img />. The gradient is deterministic
 * per-name so the same friend always shows the same color when no avatar exists,
 * which keeps things visually anchored even before they upload a picture.
 *
 * Props:
 *   src         data-URI or absolute URL of the avatar (avatar_url from API)
 *   name        used for the initial + deterministic fallback gradient
 *   size        px size (number) — default 36
 *   className   extra wrapper classes
 *   ring        adds a soft ring around the avatar (good for active states)
 */
const GRADIENTS = [
  "from-violet-500 to-pink-500",
  "from-amber-400 to-rose-500",
  "from-emerald-400 to-cyan-500",
  "from-fuchsia-500 to-purple-600",
  "from-indigo-500 to-violet-600",
  "from-pink-500 to-orange-400",
  "from-teal-400 to-blue-500",
  "from-yellow-400 to-pink-500",
];

const hashName = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (h << 5) - h + name.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const UserAvatar = ({
  src,
  name = "",
  size = 36,
  className = "",
  ring = false,
}) => {
  const initial = useMemo(() => {
    const trimmed = (name || "").trim();
    return (trimmed || "?").charAt(0).toUpperCase();
  }, [name]);

  const gradient = useMemo(() => {
    return GRADIENTS[hashName(name) % GRADIENTS.length];
  }, [name]);

  const pxStyle = { width: `${size}px`, height: `${size}px` };
  const fontSize = Math.max(10, Math.floor(size * 0.42));
  const ringClass = ring
    ? "ring-2 ring-violet-500/40 ring-offset-1 ring-offset-background"
    : "";

  return (
    <div
      className={`relative rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-white font-semibold bg-gradient-to-br ${gradient} ${ringClass} ${className}`}
      style={pxStyle}
      data-testid="user-avatar"
      aria-label={name ? `${name}'s avatar` : "avatar"}
    >
      {src ? (
        <img
          src={src}
          alt={name || "avatar"}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            // If a remote avatar 404s, hide the broken image so the gradient
            // initial shows underneath. Keeps the UI calm.
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span style={{ fontSize: `${fontSize}px` }}>{initial}</span>
      )}
    </div>
  );
};

export default UserAvatar;
